import React, { createContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  getAllocationsByItemId,
  getBudgetImpactTabData,
  getItemById,
  getItemRequestById,
  getRequestComments,
  getRequestMainInfo,
  getRequestQuestionAnswersAndBoxChoices,
} from '../../services/budgeterService.js';
import {
  getBoxChoices,
  getQuestions,
  getAdditionalQuestion
} from '../../services/budgeterService.js';
import {
  prepareRequestBody,
  prepareRequestBodyForEditFlow,
  prepareUploadedFiles,
  prepareUploadedFiles2,
  sortDropDowns
} from './Requester/requesterHelpers.js';
import { ToastComponent } from '@tylertech/forge';
import { messageManager } from '../System/messageManager/messageManager.js';
import { getAllProgramsByBudgetId } from '../../services/programService.js';
import { INITIAL_SUCCESS_DIALOG_STATE } from './budgetRequestsHelpers.js';
import { HttpStatusCode } from 'axios';
import { useUser } from '../../contexts/userContext.js';
import { useNameConventions } from '../../contexts/nameConventionsContext.js';

export const budgetRequestsContextInitState = {
  showRequester: false,
  request: null,
  requestType: null,
  lineItemId: null,
  activeStep: 0,
  flowTypeEdit: false,
  showRequestInfo: false,
};

export const BudgetRequestsContext = createContext(
  budgetRequestsContextInitState
);

const BudgetRequestsContextProvider = ({ children }) => {
  const [showRequester, setShowRequester] = useState(false);
  const [showRequestInfo, setShowRequestInfo] = useState(false);
  const [request, setRequest] = useState(null);
  const [originalRequest, setOriginalRequest] = useState(null);
  const [flowTypeEdit, setFlowTypeEdit] = useState(false);
  const [requestType, setRequestType] = useState();
  const [lineItemId, setLineItemId] = useState(null);
  const [requestId, setRequestId] = useState();
  const [activeStep, setActiveStep] = useState(0);
  const [boxChoices, setBoxChoices] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [programs, setPrograms] = useState([]);
  const [allocationGridApi, setAllocationGridApi] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedDivision, setSelectedDivision] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('');
  const [amount, setAmount] = useState(0);
  const [departmentsDD, setDepartmentsDD] = useState([]);
  const [divisionsDD, setDivisionDD] = useState([]);
  const [programsDD, setProgramsDD] = useState([]);
  const [successDialog, setSuccessDialog] = useState(INITIAL_SUCCESS_DIALOG_STATE);
  const [requestInfoId, setRequestInfoId] = useState(null);
  const [requestInfo, setRequestInfo] = useState(null);
  const [refreshRequestInfoFlag, setRefreshRequestInfoFlag] = useState(false);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [showApproveAndCommentDialog, setShowApproveAndCommentDialog] = useState(false);
  const [showConvertToPendingAndCommentDialog, setShowConvertToPendingAndCommentDialog] = useState(false);
  const [showRejectAndCommentDialog, setShowRejectAndCommentDialog] = useState(false);
  const [comment, setComment] = useState('');
  const [commentId, setCommentId] = useState(null);
  const [editAnswersOpened, setEditAnswersOpened] = useState(false);
  const [showEditComment, setShowEditComment] = useState(false);
  const [showDeleteComment, setShowDeleteComment] = useState(false);
  const [showActivateBudget, setShowActivateBudget] = useState(false);
  const [actionFailedDialog, setActionFailedDialog] = useState({ open: false, requestId: [], message: '' });
  const [groupActionFailedDialog, setGroupActionFailedDialog] = useState({ open: false, groupId: [], message: '' });
  const [allowCustomValues, setAllowCustomValues] = useState(false);
  const [reRenderSummaryData, setReRenderSummaryData] = useState(false);
  const { user } = useUser();
  const { nameConventions } = useNameConventions();

  const FTE_ALLOWED = user.organizationSettings.filter(setting => setting.key === 'fte_changes')[0].value;

  useEffect(() => {
    resetContext();
  }, [user?.activeOrganization, user?.activeBudget, user?.activeCostCenters]);

  useEffect(() => {
    if (flowTypeEdit && requestId) {
      // edit flow
      Promise.all([
        getItemRequestById(requestId),
        getAdditionalQuestion()
      ]).then((response) => {
        if (response.every(r => r.status === HttpStatusCode.Ok)) {
          const requestBody = prepareRequestBodyForEditFlow(response[0].data, user.activeBudget);
          const files = prepareUploadedFiles(requestBody.questionnaire);
          requestBody.additionalQuestion = response[1].data;
          setRequest(requestBody);
          setOriginalRequest(requestBody);
          setRequestType(response[0].data.requestType);
          setUploadedFiles(files);
          setShowRequester(true);
          setShowRequestInfo(false);
          setRequestInfoId(null);
          setRequestInfo(null);
        }
      }).catch(() => {
        ToastComponent.present({
          message: messageManager(nameConventions).BUDGET_REQUESTS.ERROR_FETCHING_REQUEST,
        });
      });
    } else if (lineItemId) {
      // create flow
      let itemData = {};
      Promise.all([
        getItemById(lineItemId),
        getAllocationsByItemId(
          Number(user?.activeBudget?.id),
          lineItemId
        ),
        getBoxChoices(),
        getQuestions(),
        getAdditionalQuestion()
      ]).then((response) => {
        if (response.every(r => r.status === HttpStatusCode.Ok)) {
          itemData = response[0].data;
          itemData.allocations = response[1].data;
          itemData.boxChoices = response[2].data;
          itemData.questionnaire = response[3].data;
          itemData.additionalQuestion = response[4].data;
          setRequest(prepareRequestBody(itemData, requestType, user?.activeBudget));
          setOriginalRequest(structuredClone(prepareRequestBody(itemData, requestType, user?.activeBudget)));
          setShowRequester(true);
        }
      })
        .catch(() => {
          ToastComponent.present({
            message: messageManager(nameConventions).BUDGET_REQUESTS.ERROR_FETCHING_LINE_ITEM,
          });
        });
    }
    getAllProgramsByBudgetId(user?.activeBudget?.id).then(
      (response) => {
        setPrograms(
          response.data.map((program) => {
            return {
              id: program.id,
              name: program.name,
              department: program.organizationLevels.split('--')[0],
              division: program.organizationLevels.split('--')[1],
            };
          })
        );
      }
    );
  }, [lineItemId, requestId]);

  useEffect(() => {
    if (programs.length === 0) return;
    setDepartmentsDD(sortDropDowns([...new Set(programs.map((x) => x.department))]));
    setDivisionDD(sortDropDowns([...new Set(programs.map((x) => x.division))]));
    setProgramsDD(sortDropDowns([...new Set(programs.map((x) => x.name))]));
  }, [programs]);

  useEffect(() => {
    if (!requestInfoId) return;
    fetchRequestInfo();
  }, [requestInfoId]);

  useEffect(() => {
    if (requestInfoId && refreshRequestInfoFlag) {
      fetchRequestInfo();
      setRefreshRequestInfoFlag(false);
    }
  }, [refreshRequestInfoFlag]);

  const fetchRequestInfo = () => {
    Promise.all([getRequestMainInfo(requestInfoId), getBudgetImpactTabData(requestInfoId), getRequestQuestionAnswersAndBoxChoices(requestInfoId), getRequestComments(requestInfoId)])
      .then((response) => {
        setRequestInfo({
          requestMainInfo: response[0].data,
          budgetImpact: response[1].data,
          questions: response[2].data,
          comments: response[3].data
        });
        const files = prepareUploadedFiles2(response[2].data);
        setUploadedFiles(files);
        setShowRequestInfo(true);
      })
      .catch(() => {
        ToastComponent.present({
          message: messageManager(nameConventions).BUDGET_REQUESTS.ERROR_FETCHING_LINE_ITEM,
        });
      });
  };

  const handleSetRequesterId = (id, editFlow) => {
    if (editFlow) {
      setRequestId(id);
    } else {
      setLineItemId(id);
    }
  };

  const resetContext = () => {
    setShowRequester(false);
    setShowRequestInfo(false);
    setRequest(null);
    setFlowTypeEdit(false);
    setRequestType(null);
    setLineItemId(null);
    setRequestId(null);
    setActiveStep(0);
    setBoxChoices([]);
    setUploadedFiles({});
    setPrograms([]);
    setAllocationGridApi(null);
    setSelectedDepartment('');
    setSelectedDivision('');
    setSelectedProgram('');
    setDepartmentsDD([]);
    setDivisionDD([]);
    setProgramsDD([]);
    setAmount(0);
    setSuccessDialog(INITIAL_SUCCESS_DIALOG_STATE);
    //setRequestInfoId(null);
    setRefreshRequestInfoFlag(false);
    setRequestInfo(null);
    setShowDeleteWarning(false);
    setComment('');
    setCommentId(null);
    setEditAnswersOpened(false);
    setShowEditComment(false);
    setShowDeleteComment(false);
    setShowActivateBudget(false);
    setAllowCustomValues(false);
  };

  const resetManualAllocationControls = () => {
    setSelectedDepartment('');
    setSelectedDivision('');
    setSelectedProgram('');
    setAmount(0);
  };

  return (
    <BudgetRequestsContext.Provider
      value={{
        activeStep,
        setActiveStep,
        showRequester,
        setShowRequester,
        showRequestInfo,
        setShowRequestInfo,
        request,
        setRequest,
        handleSetRequesterId,
        setRequestId,
        requestType,
        setRequestType,
        setFlowTypeEdit,
        boxChoices,
        uploadedFiles,
        setUploadedFiles,
        resetContext,
        programs,
        setPrograms,
        allocationGridApi,
        setAllocationGridApi,
        selectedDepartment,
        setSelectedDepartment,
        selectedDivision,
        setSelectedDivision,
        selectedProgram,
        setSelectedProgram,
        amount,
        setAmount,
        departmentsDD,
        divisionsDD,
        programsDD,
        setDivisionDD,
        setProgramsDD,
        successDialog,
        setSuccessDialog,
        requestInfoId,
        setRequestInfoId,
        refreshRequestInfo: () => setRefreshRequestInfoFlag(true),
        flowTypeEdit,
        requestInfo,
        setRequestInfo,
        FTE_ALLOWED,
        showDeleteWarning,
        setShowDeleteWarning,
        showApproveAndCommentDialog,
        setShowApproveAndCommentDialog,
        showConvertToPendingAndCommentDialog,
        setShowConvertToPendingAndCommentDialog,
        showRejectAndCommentDialog,
        setShowRejectAndCommentDialog,
        comment,
        setComment,
        commentId,
        setCommentId,
        editAnswersOpened,
        setEditAnswersOpened,
        showEditComment,
        setShowEditComment,
        showDeleteComment,
        setShowDeleteComment,
        showActivateBudget,
        setShowActivateBudget,
        originalRequest,
        actionFailedDialog,
        setActionFailedDialog,
        groupActionFailedDialog, 
        setGroupActionFailedDialog,
        resetManualAllocationControls,
        allowCustomValues, 
        setAllowCustomValues,
        reRenderSummaryData,
        setReRenderSummaryData,
      }}
    >
      {children}
    </BudgetRequestsContext.Provider>
  );
};

BudgetRequestsContextProvider.propTypes = {
  children: PropTypes.node,
};

export default BudgetRequestsContextProvider;
